
import React, { useState, useEffect } from 'react';
import { Users, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const Step5Students = ({ wizardData, updateWizardData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allStudents, setAllStudents] = useState([]);

  useEffect(() => {
    // Generate mock students from source buses
    const students = [];
    wizardData.sourceBuses?.forEach((bus) => {
      const studentCount = bus.studentCount;
      for (let i = 0; i < studentCount; i++) {
        students.push({
          id: `${bus.id}-student-${i + 1}`,
          name: `Student ${String.fromCharCode(65 + (i % 26))}${Math.floor(i / 26) + 1}`,
          busNumber: bus.busNumber,
          busId: bus.id,
          stop: `Stop ${String.fromCharCode(65 + (i % 5))}`,
          grade: `Grade ${(i % 10) + 1}`,
        });
      }
    });
    setAllStudents(students);

    // Auto-select all students initially
    if (wizardData.selectedStudents.length === 0) {
      updateWizardData('selectedStudents', students);
    }
  }, [wizardData.sourceBuses]);

  const filteredStudents = allStudents.filter((student) =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.busNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.stop.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleStudent = (student) => {
    const isSelected = wizardData.selectedStudents.some((s) => s.id === student.id);
    if (isSelected) {
      updateWizardData(
        'selectedStudents',
        wizardData.selectedStudents.filter((s) => s.id !== student.id)
      );
    } else {
      updateWizardData('selectedStudents', [...wizardData.selectedStudents, student]);
    }
  };

  const handleSelectAll = () => {
    updateWizardData('selectedStudents', filteredStudents);
  };

  const handleDeselectAll = () => {
    updateWizardData('selectedStudents', []);
  };

  const isAllSelected = filteredStudents.length > 0 && 
    filteredStudents.every((student) =>
      wizardData.selectedStudents.some((s) => s.id === student.id)
    );

  const selectedCount = wizardData.selectedStudents.length;
  const totalCount = allStudents.length;

  // Group students by bus for color coding
  const getBusColor = (busId) => {
    const colors = ['bg-blue-100', 'bg-green-100', 'bg-purple-100', 'bg-orange-100', 'bg-pink-100'];
    const index = wizardData.sourceBuses.findIndex((bus) => bus.id === busId);
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Review and select students to transfer to {wizardData.destinationBus?.busNumber}. You can
          select or deselect individual students as needed.
        </p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={isAllSelected}
          >
            Select All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedCount === 0}
          >
            Deselect All
          </Button>
          <div className="text-sm text-gray-600">
            <strong>{selectedCount}</strong> of <strong>{totalCount}</strong> students selected
          </div>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Students Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <Table>
            <TableHeader className="bg-gray-50 sticky top-0">
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleSelectAll();
                      } else {
                        handleDeselectAll();
                      }
                    }}
                  />
                </TableHead>
                <TableHead>Student Name</TableHead>
                <TableHead>Original Bus</TableHead>
                <TableHead>Stop</TableHead>
                <TableHead>Grade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    {searchTerm ? 'No students match your search' : 'No students available'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredStudents.map((student) => {
                  const isSelected = wizardData.selectedStudents.some((s) => s.id === student.id);
                  return (
                    <TableRow
                      key={student.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleToggleStudent(student)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleStudent(student)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{student.name}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getBusColor(
                            student.busId
                          )}`}
                        >
                          {student.busNumber}
                        </span>
                      </TableCell>
                      <TableCell>{student.stop}</TableCell>
                      <TableCell className="text-gray-600">{student.grade}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Summary Card */}
      {selectedCount > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Selection Summary</h4>
              <p className="text-sm text-blue-800">
                <strong>{selectedCount} students</strong> will be transferred from{' '}
                {wizardData.sourceBuses?.length} source {wizardData.sourceBuses?.length === 1 ? 'bus' : 'buses'}{' '}
                to <strong>{wizardData.destinationBus?.busNumber}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bus Legend */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <span className="text-sm font-medium text-gray-700">Bus Colors:</span>
        {wizardData.sourceBuses?.map((bus) => (
          <div key={bus.id} className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded ${getBusColor(bus.id)}`}></span>
            <span className="text-sm text-gray-600">{bus.busNumber}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Step5Students;
